import WrenchScrewdriverIcon from '@heroicons/react/24/outline/WrenchScrewdriverIcon.js';
import type { ComponentType, SVGProps } from 'react';
import React from 'react';

import { tv } from '../../tv';
import { Code, CodeBlock } from '../code';
import { Text } from '../text';

/**
 * One call an agent made to a tool, for the person reading the trace: what
 * was called, how it ended and how long it took in the header, then what
 * went in and what came back as monospace blocks. A bordered card with an
 * owned glyph, like Attachment: an object one inspects, not words someone
 * said. The status colours only the outcome; a failed call reads its
 * output in the error role because that is where the error is.
 */
const toolCallStyles = tv({
  slots: {
    root: [
      'ck:flex ck:min-w-0 ck:flex-col ck:gap-2 ck:rounded-2 ck:border ck:border-border-divider',
      'ck:surface-raised ck:p-3',
    ],
    header: 'ck:flex ck:min-w-0 ck:items-center ck:gap-2',
    glyph: 'ck:size-4 ck:shrink-0 ck:text-text-muted',
    title: 'ck:flex ck:min-w-0 ck:flex-1 ck:items-baseline ck:gap-2 ck:overflow-hidden',
    trail: 'ck:flex ck:shrink-0 ck:items-baseline ck:gap-1 ck:tabular-nums',
  },
  variants: {
    status: {
      running: {},
      done: {},
      failed: { glyph: 'ck:text-error-text' },
      cancelled: { root: 'ck:opacity-50' },
    },
  },
  defaultVariants: { status: 'done' },
});

export type ToolCallStatus = 'running' | 'done' | 'failed' | 'cancelled';

export interface ToolCallProps {
  /** The tool's name as the agent knows it, in the mono face. */
  name: string;
  status: ToolCallStatus;
  /** The status in words, in the reader's language: "Running", "Done", "Did not finish". */
  statusLabel: string;
  /** What this card is, before the name: "Tool call". */
  kindLabel?: string;
  /** How long the call took, formatted; absent while unknown. */
  duration?: string | null;
  /** The glyph for the call; a wrench by default. */
  glyph?: ComponentType<SVGProps<SVGSVGElement>>;
  /** The card's sections: `ToolCall.Input` and `ToolCall.Output`. */
  children?: React.ReactNode;
  id?: string;
}

const Root = React.forwardRef<HTMLDivElement, ToolCallProps>(function ToolCall(
  {
    name,
    status,
    statusLabel,
    kindLabel,
    duration,
    glyph: Glyph = WrenchScrewdriverIcon,
    children,
    ...props
  },
  ref,
) {
  const { root, header, glyph, title, trail } = toolCallStyles({ status });
  return (
    <div {...props} ref={ref} className={root()} data-component="tool-call" data-status={status}>
      <div className={header()}>
        <Glyph className={glyph()} aria-hidden="true" />
        <div className={title()}>
          {kindLabel ? (
            <Text size="xs" color="muted" isNoWrap>
              {kindLabel}
            </Text>
          ) : null}
          <Code>{name}</Code>
        </div>
        <div className={trail()}>
          <Text
            size="xs"
            color={status === 'failed' ? 'error' : status === 'running' ? 'default' : 'muted'}
            weight={status === 'failed' ? 'medium' : 'regular'}
            isLive={status === 'running'}
          >
            {statusLabel}
          </Text>
          {duration ? (
            <Text size="xs" color="muted">
              · {duration}
            </Text>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  );
});

export interface ToolCallSectionProps {
  /** What the block holds: "Input", "Output". */
  label: string;
  /** Text keeps its breaks; anything else is shown as formatted JSON. */
  value: unknown;
  id?: string;
}

/** What went in: short, so it shows at a glance. */
const Input = React.forwardRef<HTMLDivElement, ToolCallSectionProps>(function ToolCallInput(
  { label, value, ...props },
  ref,
) {
  return <CodeBlock {...props} ref={ref} label={label} value={value} maxHeight="glance" />;
});

export interface ToolCallOutputProps extends ToolCallSectionProps {
  /** The output is the error the call ended with. */
  isError?: boolean;
}

/** What came back: longer, so it shows a reading's worth and scrolls. */
const Output = React.forwardRef<HTMLDivElement, ToolCallOutputProps>(function ToolCallOutput(
  { label, value, isError = false, ...props },
  ref,
) {
  return (
    <CodeBlock
      {...props}
      ref={ref}
      label={label}
      value={value}
      maxHeight="reading"
      tone={isError ? 'error' : 'default'}
    />
  );
});

export const ToolCall = Object.assign(Root, { Input, Output });
